import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api, isApiError, loginResponseSchema, type User, userSchema } from '@/lib/api';

// The signed-in person (GET /internal/auth/me). 401 means "nobody": the
// query resolves to null instead of failing, so routes can redirect.

export const meQueryKey = ['auth', 'me'] as const;

export const meQuery = queryOptions({
  queryKey: meQueryKey,
  queryFn: async (): Promise<User | null> => {
    try {
      return await api('/internal/auth/me', { schema: userSchema, quiet401: true });
    } catch (error) {
      if (isApiError(error) && error.status === 401) return null;
      throw error;
    }
  },
  staleTime: 60_000,
  retry: false,
});

export interface LoginInput {
  email: string;
  password: string;
  totp?: string;
  /** "Remember me for 30 days" (07 §1.1): 30 days fixed; without it the session slides, 12 h after the last request. */
  remember?: boolean;
}

export async function login(queryClient: QueryClient, input: LoginInput): Promise<User> {
  const body: Record<string, unknown> = { email: input.email.trim(), password: input.password };
  if (input.totp) body.totp = input.totp;
  if (input.remember) body.remember = true;
  const result = await api('/internal/auth/login', {
    method: 'POST',
    body,
    schema: loginResponseSchema,
    quiet401: true,
  });
  queryClient.setQueryData(meQueryKey, result.user);
  return result.user;
}

/** ADR 0059: the temporary password, then the person's own (12–256 characters). */
export async function changePassword(
  queryClient: QueryClient,
  input: { current: string; next: string },
): Promise<User> {
  const user = await api('/internal/auth/password', {
    method: 'POST',
    body: { current_password: input.current, new_password: input.next },
    schema: userSchema,
  });
  queryClient.setQueryData(meQueryKey, user);
  return user;
}

const totpSetupSchema = z.looseObject({ secret: z.string(), otpauth_url: z.string() });
export type TotpSetup = z.infer<typeof totpSetupSchema>;

/** ADR 0067, step 1: a new secret to scan; nothing is stored until a code confirms it. */
export function startTotpSetup(): Promise<TotpSetup> {
  return api('/internal/auth/totp/setup', { method: 'POST', schema: totpSetupSchema });
}

/** Step 2: a code from the app confirms the secret; the same session then opens Plancia. */
export async function confirmTotp(
  queryClient: QueryClient,
  input: { secret: string; code: string },
): Promise<User> {
  const user = await api('/internal/auth/totp/confirm', {
    method: 'POST',
    body: { secret: input.secret, code: input.code },
    schema: userSchema,
  });
  queryClient.setQueryData(meQueryKey, user);
  return user;
}

export async function logout(queryClient: QueryClient): Promise<void> {
  try {
    await api('/internal/auth/logout', { method: 'POST', quiet401: true });
  } catch {
    /* the session may already be gone: signing out locally is enough */
  }
  queryClient.setQueryData(meQueryKey, null);
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
}

/** The login error codes the console tells apart (Problem Details `code`). */
export type LoginErrorKind =
  | 'credentials'
  | 'totp_required'
  | 'totp_invalid'
  | 'locked'
  | 'network'
  | 'other';

export function loginErrorKind(error: unknown): LoginErrorKind {
  if (!isApiError(error)) return 'other';
  if (error.isNetwork) return 'network';
  switch (error.code) {
    case 'invalid_credentials':
      return 'credentials';
    case 'totp_required':
      return 'totp_required';
    case 'invalid_totp':
      return 'totp_invalid';
    // Not sent by the API yet (lockout after 5 attempts, 07 S00): ready for it.
    case 'account_locked':
    case 'too_many_attempts':
      return 'locked';
    default:
      return error.status === 429 ? 'locked' : 'other';
  }
}
